import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { CaretDown, NotePencil } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import { AssessmentSubmissionQuestion, UpdatedEntityType } from "../../../../graphql/generated/graphql";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import type { GradeChangeParams, GradeTree } from "./types";

interface QuestionCardProps {
  question: AssessmentSubmissionQuestion;
  gradeTreeQuestion: GradeTree["questions"][number];
  questionNumber: string;
  setActivelyEditedGradeElement: React.Dispatch<React.SetStateAction<string>>;
  activelyEditedGradeElement: string;
  handleGradeChange: (input: GradeChangeParams) => void;
  handleSave: (closeModal: boolean) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  gradeTreeQuestion,
  setActivelyEditedGradeElement,
  activelyEditedGradeElement,
  questionNumber,
  handleGradeChange,
  handleSave,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleScoreInputFocus = (elementId: string) => {
    setActivelyEditedGradeElement(elementId);
  };

  return (
    <View style={styles.questionCard}>
      <View style={styles.questionHeader}>
        <Pressable style={styles.questionInfo} onPress={toggleExpansion}>
          <CustomText
            text={questionNumber}
            size="xs"
            weight="bold"
            style={styles.questionNumber}
            useTranslationText={false}
          />
          {question.question?.startsWith("Question no longer available") && (
            <View style={styles.retiredBadge}>
              <CustomText
                text="Retired question"
                size="xs"
                style={styles.retiredBadgeText}
                useTranslationText={false}
              />
            </View>
          )}
          <CustomText text={question.question} size="s" style={styles.questionText} useTranslationText={false} />
        </Pressable>
        <View style={styles.questionScore}>
          {activelyEditedGradeElement === question.id ? (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
              <CustomTextInput
                value={gradeTreeQuestion?.totalPoints}
                onChangeText={(text) =>
                  handleGradeChange({
                    updatedEntityType: UpdatedEntityType.Question,
                    updatedEntityId: question.id,
                    updatedGrade: text,
                    maxPointsForEntity: gradeTreeQuestion?.maximumPoints,
                  })
                }
                placeholder=""
                keyboardType="numeric"
                style={{ width: 60 }}
                onBlur={() => handleSave(false)}
              />
              <CustomText text={`/${gradeTreeQuestion?.maximumPoints}`} weight="bold" useTranslationText={false} />
            </View>
          ) : (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <CustomText
                text={`${gradeTreeQuestion?.totalPoints}/${gradeTreeQuestion?.maximumPoints || 10}`}
                size="s"
                weight="bold"
                style={styles.aiScore}
                useTranslationText={false}
              />
              <Pressable onPress={() => handleScoreInputFocus(question.id)}>
                <NotePencil size={16} color="#9CA3AF" />
              </Pressable>
            </View>
          )}
        </View>
        <Pressable onPress={toggleExpansion}>
          <CaretDown
            size={16}
            color="#9CA3AF"
            style={isExpanded ? styles.questionToggleRotated : styles.questionToggle}
          />
        </Pressable>
      </View>

      {isExpanded && (
        <View style={styles.questionContent}>
          {/* Student Answer Section */}
          <View style={styles.studentAnswerSection}>
            <CustomText text="Student Answer" size="s" weight="bold" style={styles.sectionTitle} />
            <View style={styles.studentAnswer}>
              <CustomText
                text={question.userAnswer?.answer || ""}
                size="s"
                style={styles.studentAnswerText}
                useTranslationText={false}
              />
            </View>
          </View>

          {/* Grading Criteria Section */}
          <View style={styles.gradingCriteriaSection}>
            <CustomText text="Grading Criteria" size="s" weight="bold" style={styles.sectionTitle} />
            <View style={styles.criteriaList}>
              {question.rubrics?.map((rubric, index) => {
                const gradeTreeRubric = gradeTreeQuestion?.rubrics?.find((r) => r.id === rubric.id);

                return (
                  <View key={index} style={styles.criterionItem}>
                    <CustomText
                      text={rubric.component}
                      size="s"
                      style={styles.criterionName}
                      useTranslationText={false}
                    />
                    {activelyEditedGradeElement === rubric.id ? (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                        <CustomTextInput
                          value={gradeTreeRubric?.pointsAwarded}
                          onChangeText={(text) =>
                            handleGradeChange({
                              updatedEntityType: UpdatedEntityType.Rubric,
                              updatedEntityId: rubric.id,
                              updatedGrade: text,
                              maxPointsForEntity: gradeTreeRubric?.maxPoints,
                            })
                          }
                          placeholder=""
                          keyboardType="numeric"
                          style={{ width: 60 }}
                          onBlur={() => handleSave(false)}
                        />
                        <CustomText text={`/${gradeTreeRubric?.maxPoints}`} weight="bold" useTranslationText={false} />
                      </View>
                    ) : (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                        <CustomText
                          text={`${gradeTreeRubric?.pointsAwarded}/${gradeTreeRubric?.maxPoints}`}
                          size="s"
                          weight="bold"
                          style={styles.criterionScore}
                          useTranslationText={false}
                        />
                        <Pressable onPress={() => handleScoreInputFocus(rubric.id)}>
                          <NotePencil size={16} color="#9CA3AF" />
                        </Pressable>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  questionCard: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  questionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
  },
  questionInfo: {
    flex: 1,
    flexDirection: "column",
    gap: 8,
  },
  questionNumber: {
    color: "#3E68FE",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  questionText: {
    color: "#101828",
    lineHeight: 22,
  },
  retiredBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#FEF3C7",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 4,
  },
  retiredBadgeText: {
    color: "#92400E",
  },
  questionScore: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  },
  aiScore: {
    color: "#059669",
    backgroundColor: "#D1FAE5",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  questionToggle: {
    // No transition needed for React Native
  },
  questionToggleRotated: {
    transform: [{ rotate: "180deg" }],
  },
  questionContent: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  studentAnswerSection: {
    padding: 20,
  },
  sectionTitle: {
    color: "#111827",
    marginBottom: 12,
    letterSpacing: 0.5,
  },
  studentAnswer: {
    backgroundColor: "#F9FAFB",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  studentAnswerText: {
    color: "#374151",
    lineHeight: 22,
  },
  gradingCriteriaSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  criteriaList: {
    flexDirection: "column",
    gap: 8,
  },
  criterionItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#FCFCFD",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
  },
  criterionName: {
    color: "#374151",
  },
  criterionScore: {
    color: "#059669",
  },
});

export default QuestionCard;
